// Hand-authored first level, carved programmatically so the grid stays
// guaranteed-enclosed and fully connected (easy to verify + extend). Rooms and
// corridors are carved out of a solid block; a few faces are re-skinned with
// accent/hazard textures and interior pillars give cover and depth cues.

import { TEX } from "./textures";
import type { EnemySpawn, Level } from "./types";

const W = 28;
const H = 22;

export function buildLevel(): Level {
  const cells = new Uint8Array(W * H).fill(TEX.WALL_PANEL);

  const set = (x: number, y: number, v: number): void => {
    if (x <= 0 || y <= 0 || x >= W - 1 || y >= H - 1) return; // keep the border solid
    cells[y * W + x] = v;
  };
  const carve = (x0: number, y0: number, x1: number, y1: number): void => {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) set(x, y, 0);
  };

  // Rooms.
  carve(2, 15, 9, 20); // A: start room (bottom-left)
  carve(2, 2, 8, 7); // B: top-left room
  carve(12, 2, 25, 8); // C: top arena (widest fight space)
  carve(16, 11, 25, 20); // D: right hall
  carve(11, 13, 14, 20); // E: connector room

  // Corridors.
  carve(5, 7, 5, 15); // A/E area <-> B (vertical spine, left)
  carve(9, 17, 16, 17); // A -> D (horizontal, lower)
  carve(6, 4, 12, 4); // B -> C (upper)
  carve(12, 8, 12, 13); // C -> E
  carve(20, 8, 20, 11); // C -> D (right drop)
  carve(12, 18, 16, 18); // E -> D

  // Interior pillars for cover + parallax (accent-skinned).
  const pillars: ReadonlyArray<readonly [number, number]> = [
    [18, 5],
    [22, 5],
    [20, 13],
    [23, 16],
    [4, 18],
  ];
  for (const [px, py] of pillars) {
    set(px, py, TEX.WALL_TRIM);
  }

  // A couple of hazard-lit wall faces near choke points.
  set(12, 9, TEX.WALL_HAZARD);
  set(15, 17, TEX.WALL_HAZARD);

  // Accent trim framing the start-room mouth.
  set(5, 14, TEX.WALL_TRIM);

  // Competitor parody archetypes seeded across the map (kinds resolve to the
  // ZTNA/SSE roster in sprites.ts). Different incumbent guards each room.
  const enemies: EnemySpawn[] = [
    { x: 5.5, y: 4.5, kind: "netscope" }, // room B
    { x: 17.5, y: 4.5, kind: "pale-alto" }, // arena C
    { x: 23.5, y: 6.5, kind: "zeescaler" }, // arena C far
    { x: 22.5, y: 15.5, kind: "sysko" }, // room D
    { x: 12.5, y: 16.5, kind: "netscope" }, // connector E
  ];

  return {
    width: W,
    height: H,
    cells,
    floorTexId: TEX.FLOOR,
    ceilTexId: TEX.CEIL,
    player: { x: 5.5, y: 18.5, angle: Math.PI * 1.5 }, // stand in A, face north up the spine
    enemies,
  };
}
